/**
 * Progetti archive — AJAX pagination and featured carousel.
 *
 * @package HelloElementorChild
 */

import { initCamstReveal, refreshCamstReveal } from './camst-reveal.js';

var progettiInstances = new WeakMap();
var progettiRequestIds = new WeakMap();

function getState(root) {
	return progettiInstances.get(root) || { page: parseInt(root.getAttribute('data-initial-page') || '1', 10) || 1 };
}

function setState(root, state) {
	progettiInstances.set(root, state);
}

function nextRequestId(root) {
	var next = (progettiRequestIds.get(root) || 0) + 1;
	progettiRequestIds.set(root, next);
	return next;
}

function isLatestRequest(root, requestId) {
	return progettiRequestIds.get(root) === requestId;
}

function bindFeaturedCarousel(root) {
	var track = root.querySelector('[data-camst-featured-track]');

	if (!track) {
		return;
	}

	var prev = root.querySelector('[data-camst-featured-prev]');
	var next = root.querySelector('[data-camst-featured-next]');

	function scrollBySlide(direction) {
		track.scrollBy({
			left: direction * track.clientWidth,
			behavior: 'smooth',
		});
	}

	if (prev) {
		prev.addEventListener('click', function () {
			scrollBySlide(-1);
		});
	}

	if (next) {
		next.addEventListener('click', function () {
			scrollBySlide(1);
		});
	}
}

function buildPaginationHtml(pagination) {
	var current = pagination.current || 1;
	var total = pagination.total || 1;

	if (total <= 1) {
		return '<nav class="camst-progetti-archive__pagination" data-camst-progetti-pagination aria-label="Paginazione progetti" hidden></nav>';
	}

	var html = '<nav class="camst-progetti-archive__pagination" data-camst-progetti-pagination aria-label="Paginazione progetti">';
	html +=
		'<button type="button" class="camst-progetti-archive__page-btn camst-progetti-archive__page-btn--nav" data-page="' +
		Math.max(1, current - 1) +
		'"' +
		(current <= 1 ? ' disabled' : '') +
		' aria-label="Pagina precedente"><i class="fas fa-chevron-left" aria-hidden="true"></i></button>';

	getPaginationWindow(current, total).forEach(function (pageNumber) {
		if (pageNumber === '…') {
			html += '<span class="camst-progetti-archive__page-ellipsis" aria-hidden="true">…</span>';
			return;
		}

		var isActive = pageNumber === current;
		html +=
			'<button type="button" class="camst-progetti-archive__page-btn' +
			(isActive ? ' is-active' : '') +
			'" data-page="' +
			pageNumber +
			'"' +
			(isActive ? ' aria-current="page"' : '') +
			'>' +
			pageNumber +
			'</button>';
	});

	html +=
		'<button type="button" class="camst-progetti-archive__page-btn camst-progetti-archive__page-btn--nav" data-page="' +
		Math.min(total, current + 1) +
		'"' +
		(current >= total ? ' disabled' : '') +
		' aria-label="Pagina successiva"><i class="fas fa-chevron-right" aria-hidden="true"></i></button>';
	html += '</nav>';

	return html;
}

function getPaginationWindow(current, total) {
	if (total <= 5) {
		return Array.from({ length: total }, function (_, index) {
			return index + 1;
		});
	}

	var pages = [1];

	if (current > 3) {
		pages.push('…');
	}

	for (var i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
		pages.push(i);
	}

	if (current < total - 2) {
		pages.push('…');
	}

	pages.push(total);

	return pages.filter(function (value, index, array) {
		return array.indexOf(value) === index;
	});
}

function bindPagination(root, state) {
	var pagination = root.querySelector('[data-camst-progetti-pagination]');

	if (!pagination) {
		return;
	}

	pagination.addEventListener('click', function (event) {
		var button = event.target.closest('[data-page]');

		if (!button || button.disabled || !root.contains(button)) {
			return;
		}

		var page = parseInt(button.getAttribute('data-page') || '1', 10) || 1;

		if (page === state.page) {
			return;
		}

		fetchArchive(root, { page: page }, { scroll: true });
	});
}

function fetchArchive(root, nextState, options) {
	var opts = options || {};
	var requestId = nextRequestId(root);
	var restUrl = root.getAttribute('data-rest-url');
	var perPage = parseInt(root.getAttribute('data-per-page') || '6', 10) || 6;
	var grid = root.querySelector('[data-camst-progetti-grid]');
	var url = new URL(restUrl, window.location.origin);

	url.searchParams.set('page', String(nextState.page || 1));
	url.searchParams.set('per_page', String(perPage));
	url.searchParams.set('_', String(requestId)); // cache-buster: risposta sempre fresca

	root.classList.add('is-loading');

	return fetch(url.toString(), {
		credentials: 'same-origin',
		cache: 'no-store',
		headers: {
			Accept: 'application/json',
		},
	})
		.then(function (response) {
			if (!response.ok) {
				throw new Error('Progetti request failed');
			}

			return response.json();
		})
		.then(function (payload) {
			if (!isLatestRequest(root, requestId)) {
				return;
			}

			if (payload.code) {
				throw new Error(payload.message || 'Progetti request failed');
			}

			if (grid) {
				grid.innerHTML = payload.html || '';
			}

			var paginationWrap = root.querySelector('[data-camst-progetti-pagination]');

			if (paginationWrap && paginationWrap.parentNode) {
				paginationWrap.outerHTML = buildPaginationHtml(payload.pagination || { current: 1, total: 1 });
			}

			var newState = { page: payload.pagination?.current || nextState.page || 1 };
			setState(root, newState);
			bindPagination(root, newState);

			if (payload.url) {
				window.history.pushState(newState, '', payload.url);
			}

			refreshCamstReveal(root);

			if (opts.scroll) {
				var anchor = grid || root.querySelector('.camst-progetti-archive__portfolio');

				if (anchor) {
					var scrollToAnchor = function () {
						var header = document.querySelector('.camst-header, header[role="banner"], .site-header');
						var headerOffset = header ? header.getBoundingClientRect().height : 0;
						var top = anchor.getBoundingClientRect().top + window.pageYOffset - headerOffset - 24;

						window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
					};

					// Dopo il refresh di ScrollTrigger e il reflow: scroll + re-assert
					// per battere lo scroll-anchoring delle immagini lazy.
					requestAnimationFrame(scrollToAnchor);
					window.setTimeout(scrollToAnchor, 350);
				}
			}
		})
		.catch(function () {
			if (!isLatestRequest(root, requestId)) {
				return;
			}
		})
		.finally(function () {
			if (isLatestRequest(root, requestId)) {
				root.classList.remove('is-loading');
			}
		});
}

export function initCamstProgettiArchive(scope) {
	var roots = [];

	if (!scope) {
		roots = Array.prototype.slice.call(document.querySelectorAll('[data-camst-progetti-archive]'));
	} else if (scope.matches && scope.matches('[data-camst-progetti-archive]')) {
		roots = [scope];
	} else {
		roots = Array.prototype.slice.call(scope.querySelectorAll('[data-camst-progetti-archive]'));
	}

	roots.forEach(function (root) {
		if (root.dataset.camstProgettiArchiveInit === '1') {
			return;
		}

		var state = getState(root);
		setState(root, state);
		bindFeaturedCarousel(root);
		bindPagination(root, state);
		initCamstReveal(root);
		root.dataset.camstProgettiArchiveInit = '1';
	});
}

export function teardownCamstProgettiArchive(scope) {
	var roots = scope
		? scope.matches && scope.matches('[data-camst-progetti-archive]')
			? [scope]
			: Array.prototype.slice.call(scope.querySelectorAll('[data-camst-progetti-archive]'))
		: [];

	roots.forEach(function (root) {
		delete root.dataset.camstProgettiArchiveInit;
	});
}
